import React, { useState, useEffect, useMemo, useLayoutEffect, useRef } from 'react';
import { 
  TrendingUp, Coins, ShoppingCart, Package, Users, 
  Star, Tag, MessageSquare, Calendar, Download,
  TrendingDown, AlertCircle, CheckCircle, Clock, BarChart3,
  Activity, Target, Percent, Zap, RefreshCw, 
  TrendingUpIcon
} from 'lucide-react';
import { 
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  Area, AreaChart, RadarChart, PolarGrid, PolarAngleAxis, 
  PolarRadiusAxis, Radar, ComposedChart
} from 'recharts';
import { analyticsAPI } from '../../services/api';
import './css/AdminDashboard.css';

const safeArray = (value) => (Array.isArray(value) ? value : []);

const asNumber = (value, fallback = 0) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

// Custom Hook for Mount Animation
const useIsMounted = () => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(timer);
  }, []);
  return mounted;
};

const getChartHeights = () => {
  if (typeof window === 'undefined') {
    return { regular: 260, large: 300 };
  }

  const width = window.innerWidth;
  if (width <= 480) return { regular: 200, large: 220 };
  if (width <= 768) return { regular: 230, large: 260 };
  return { regular: 260, large: 300 };
};

const useChartHeights = () => {
  const [heights, setHeights] = useState(getChartHeights);

  useEffect(() => {
    const handleResize = () => {
      const next = getChartHeights();
      setHeights((prev) =>
        prev.regular === next.regular && prev.large === next.large ? prev : next
      );
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return heights;
};

const useElementSize = (ref) => {
  const [size, setSize] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    const updateSize = () => {
      const rect = element.getBoundingClientRect();
      const next = {
        width: Math.floor(rect.width),
        height: Math.floor(rect.height)
      };
      setSize((prev) =>
        prev.width === next.width && prev.height === next.height ? prev : next
      );
    };

    updateSize();

    let observer;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => updateSize());
      observer.observe(element);
    } else {
      window.addEventListener('resize', updateSize);
    }

    return () => {
      if (observer) {
        observer.disconnect();
      } else {
        window.removeEventListener('resize', updateSize);
      }
    };
  }, [ref]);

  return size;
};

const ChartSkeleton = ({ height = '100%' }) => (
  <div className="chart-skeleton" style={{ height }}>
    <div className="skeleton-shimmer" />
  </div>
);

const AnalyticsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [refreshing, setRefreshing] = useState(false);
  const mounted = useIsMounted();
  const chartHeights = useChartHeights();

  useEffect(() => {
    fetchDashboardData();
  }, [period]);

  useEffect(() => {
    // Simulate chart loading for smoother UX
    setChartLoading(true);
    const timer = setTimeout(() => setChartLoading(false), 500);
    return () => clearTimeout(timer);
  }, [dashboardData, activeTab]);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await analyticsAPI.getDashboard(period);
      setDashboardData(response.data);
    } catch (err) {
      setError('Impossible de charger les donnees analytiques');
      console.error('Analytics error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDashboardData();
    setTimeout(() => setRefreshing(false), 600);
  };

  const handleExport = async (type) => {
    try {
      const response = await analyticsAPI.exportData(type, period);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_report_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Export error:', err);
    }
  };

  const periodLabel = useMemo(() => {
    const labels = {
      today: "Aujourd'hui",
      week: '7 derniers jours',
      month: '30 derniers jours',
      quarter: '3 derniers mois',
      year: 'Annee derniere'
    };
    return labels[period];
  }, [period]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-glass">
          <div className="spinner" />
          <p className="loading-text">Chargement des donnees analytiques...</p>
          <div className="loading-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-glass">
          <AlertCircle size={48} className="error-icon" />
          <p className="error-text">{error}</p>
          <button onClick={fetchDashboardData} className="retry-button">
            <RefreshCw size={16} />
            <span>Reessayer</span>
          </button>
        </div>
      </div>
    );
  }

  if (!dashboardData) return null;

  const data = dashboardData?.data ?? dashboardData ?? {};
  const revenue = data.revenue ?? {};
  const orders = data.orders ?? {};
  const products = data.products ?? {};
  const customers = data.customers ?? {};
  const reviews = data.reviews ?? {};
  const promos = data.promos ?? {};
  const chat = data.chat ?? {};
  const delivery = data.delivery ?? {};
  const categoriesList = safeArray(data.categories ?? data.categoryPerformance ?? data.category_performance);
  const dailyTrendList = safeArray(data.dailyTrend ?? data.daily_trend ?? data.trend ?? data.salesOverTime);

  const ordersByStatus = safeArray(
    orders.byStatus ?? orders.by_status ?? orders.statusBreakdown ?? orders.status_breakdown
  );
  const ordersByUserType = safeArray(
    orders.byUserType ?? orders.by_user_type ?? orders.userTypeBreakdown ?? orders.user_type_breakdown
  );
  const topSelling = safeArray(
    products.topSelling ?? products.top_selling ?? products.topProducts ?? products.top_products
  );

  const revenueGross = asNumber(revenue.gross ?? revenue.gross_revenue ?? revenue.grossRevenue);
  const revenueNet = asNumber(revenue.net ?? revenue.net_revenue ?? revenue.netRevenue);
  const revenueDiscounts = asNumber(
    revenue.discounts ?? revenue.total_discounts ?? revenue.totalDiscounts ?? revenue.total_discount_given
  );
  const revenueDeliveryFees = asNumber(
    revenue.deliveryFees ?? revenue.delivery_fees ?? revenue.total_delivery_fees
  );
  const revenueAvgOrderValue = asNumber(
    revenue.avgOrderValue ?? revenue.avg_order_value ?? revenue.average_order_value
  );

  const ordersTotalFromStatus = ordersByStatus.reduce(
    (sum, status) => sum + asNumber(status.count ?? status.order_count ?? status.orders ?? status.value),
    0
  );
  const ordersTotal = asNumber(
    orders.total ?? orders.total_orders ?? orders.totalOrders,
    ordersTotalFromStatus
  );

  const productsTotal = asNumber(products.total ?? products.total_products ?? products.totalProducts);
  const productsOutOfStock = asNumber(
    products.outOfStock ?? products.out_of_stock ?? products.outOfStockCount
  );
  const productsLowStock = asNumber(products.lowStock ?? products.low_stock ?? products.lowStockCount);

  const customersTotal = asNumber(customers.total ?? customers.total_users ?? customers.totalUsers);
  const customersNew = asNumber(customers.new ?? customers.new_users ?? customers.newUsers);

  const reviewsTotal = asNumber(reviews.total ?? reviews.total_reviews ?? reviews.reviewCount);
  const reviewsAvgRating = asNumber(reviews.avgRating ?? reviews.avg_rating ?? reviews.average_rating);
  const reviewsProductsWithReviews = asNumber(
    reviews.productsWithReviews ?? reviews.products_with_reviews ?? reviews.productsReviewed ?? reviews.products_reviewed
  );

  const promosActive = asNumber(promos.active ?? promos.active_promos ?? promos.activePromos);
  const promosTotalUses = asNumber(promos.totalUses ?? promos.total_uses ?? promos.totalUsesCount);

  const chatTotalConversations = asNumber(
    chat.totalConversations ?? chat.total_conversations ?? chat.conversationCount
  );
  const chatOpen = asNumber(chat.open ?? chat.open_conversations ?? chat.openCount);
  const chatClosed = asNumber(chat.closed ?? chat.closed_conversations ?? chat.closedCount);
  const chatTotalMessages = asNumber(chat.totalMessages ?? chat.total_messages ?? chat.messageCount);

  const deliveryTotalRevenue = asNumber(
    delivery.totalRevenue ?? delivery.total_delivery_revenue ?? delivery.total_revenue
  );
  const deliveryFreeDeliveries = asNumber(
    delivery.freeDeliveries ?? delivery.free_deliveries ?? delivery.free
  );
  const deliveryPaidDeliveries = asNumber(
    delivery.paidDeliveries ?? delivery.paid_deliveries ?? delivery.paid
  );
  const deliveryAvgFee = asNumber(delivery.avgFee ?? delivery.avg_delivery_fee ?? delivery.avg_fee);

  const COLORS = {
    primary: '#E53E3E',
    primaryLight: '#FC8181',
    success: '#38A169',
    warning: '#D69E2E',
    info: '#3182CE',
    purple: '#805AD5',
    pink: '#D53F8C',
    teal: '#319795',
    indigo: '#5A67D8',
    gray: '#718096',
    amber: '#D69E2E',
    cyan: '#3182CE'
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending':
        return 'En attente';
      case 'processing':
        return 'En cours';
      case 'shipped':
        return 'Expedie';
      case 'delivered':
        return 'Livre';
      case 'cancelled':
        return 'Annule';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  const getUserTypeLabel = (type) => {
    switch (type) {
      case 'Guest':
        return 'Invite';
      case 'Registered':
        return 'Inscrit';
      default:
        return type;
    }
  };

  const statusData = ordersByStatus.map((status) => {
    const statusKey = status.status ?? status.order_status ?? status.state ?? status.name ?? 'unknown';
    const value = asNumber(status.count ?? status.order_count ?? status.orders ?? status.value);
    const revenueValue = asNumber(status.revenue ?? status.total_revenue ?? status.amount);
    const colorKey = statusKey === 'delivered' ? 'success' : statusKey === 'pending' ? 'warning' : 'primary';
    return {
      name: getStatusLabel(statusKey),
      value,
      revenue: revenueValue,
      color: COLORS[colorKey]
    };
  });

  const userTypeData = ordersByUserType.map((type) => {
    const typeKey = type.type ?? type.user_type ?? type.customer_type ?? type.name ?? 'Guest';
    return {
      name: getUserTypeLabel(typeKey),
      orders: asNumber(type.count ?? type.order_count ?? type.orders ?? type.value),
      revenue: asNumber(type.revenue ?? type.total_revenue ?? type.amount)
    };
  });

  const topProductsData = topSelling.slice(0, 8).map((product) => {
    const productName = product.name ?? product.title ?? 'Produit';
    return {
      name: productName.length > 20 ? productName.substring(0, 20) + '...' : productName,
      sales: asNumber(
        product.unitsSold ?? product.units_sold ?? product.sales ?? product.units ?? product.qty
      ),
      revenue: asNumber(product.revenue ?? product.total_revenue ?? product.amount),
      orders: asNumber(product.orderCount ?? product.order_count ?? product.orders ?? product.orderCount)
    };
  });

  const categoryData = categoriesList.slice(0, 8).map((category) => {
    const categoryName = category.name ?? category.category ?? category.title ?? 'Categorie';
    return {
      name: categoryName.length > 15 ? categoryName.substring(0, 15) + '...' : categoryName,
      value: asNumber(category.revenue ?? category.total_revenue ?? category.amount),
      products: asNumber(category.productCount ?? category.product_count ?? category.products),
      sales: asNumber(category.unitsSold ?? category.units_sold ?? category.sales ?? category.units),
      color: COLORS[Object.keys(COLORS)[Math.floor(Math.random() * Object.keys(COLORS).length)]]
    };
  });

  const trendData = dailyTrendList.slice(-14).map((trend) => {
    const rawDate = trend.date ?? trend.period ?? trend.day ?? trend.label;
    const dateObj = rawDate ? new Date(rawDate) : null;
    const dateLabel = dateObj && !Number.isNaN(dateObj.getTime())
      ? dateObj.toLocaleDateString('fr-FR', { month: 'short', day: 'numeric' })
      : String(rawDate ?? '');
    return {
      date: dateLabel,
      dateObj: dateObj ?? null,
      revenue: asNumber(
        trend.revenue ?? trend.total_revenue ?? trend.net_revenue ?? trend.gross_revenue
      ),
      orders: asNumber(trend.orders ?? trend.order_count ?? trend.orders_count ?? trend.count),
      units: asNumber(trend.unitsSold ?? trend.units_sold ?? trend.units ?? trend.units_sold_30days)
    };
  });

  const deliveredCount = ordersByStatus.find((status) => {
    const statusKey = status.status ?? status.order_status ?? status.state ?? status.name;
    return statusKey === 'delivered';
  });
  const deliveredTotal = asNumber(
    deliveredCount?.count ?? deliveredCount?.order_count ?? deliveredCount?.orders ?? deliveredCount?.value
  );

  const conversionRate = ordersTotal > 0
    ? ((deliveredTotal / ordersTotal) * 100).toFixed(2)
    : '0';

  const discountRate = revenueGross > 0 ? (revenueDiscounts / revenueGross) * 100 : 0;

  const guestVsRegistered = ordersByUserType.map((type) => {
    const typeKey = type.type ?? type.user_type ?? type.customer_type ?? type.name ?? 'Guest';
    const count = asNumber(type.count ?? type.order_count ?? type.orders ?? type.value);
    return {
      type: typeKey,
      percentage: ordersTotal > 0 ? ((count / ordersTotal) * 100).toFixed(1) : '0'
    };
  });

  const performanceData = [
    { metric: 'Ventes', value: Math.min(100, parseFloat(conversionRate) * 1.2), fullMark: 100 },
    { metric: 'Produits', value: productsTotal > 0 ? ((productsTotal - productsOutOfStock) / productsTotal * 100) : 0, fullMark: 100 },
    { metric: 'Avis', value: reviewsAvgRating * 20, fullMark: 100 },
    { metric: 'Support', value: chatOpen > 0 ? Math.max(0, 100 - (chatOpen * 5)) : 100, fullMark: 100 },
    { metric: 'Promos', value: promosActive > 0 ? 75 : 50, fullMark: 100 },
    { metric: 'Livraison', value: deliveryPaidDeliveries > 0 ? 80 : 60, fullMark: 100 }
  ];

  const chartHeight = chartHeights.regular;
  const chartLargeHeight = chartHeights.large;

  return (
    <div className="analytics-container">
      {/* Enhanced Navigation Tabs - Fixed Controls */}
      <nav className={`analytics-tabs ${mounted ? 'mounted' : ''}`}>
        <div className="tabs-list">
          {[
            { id: 'overview', label: 'Apercu', icon: Activity },
            { id: 'sales', label: 'Ventes', icon: Coins },
            { id: 'products', label: 'Produits', icon: Package },
            { id: 'customers', label: 'Clients', icon: Users }
          ].map(tab => (
            <button
              key={tab.id}
              className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
              aria-pressed={activeTab === tab.id}
              title={tab.label}
            >
              <tab.icon size={16} />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        <div className="tab-controls">
          <button onClick={handleRefresh} className="refresh-button" disabled={refreshing} aria-label="Rafraichir les donnees">
            <RefreshCw size={14} className={refreshing ? 'spinning' : ''} />
          </button>

          <div className="period-selector">
            <Calendar size={14} />
            <select 
              value={period} 
              onChange={(e) => setPeriod(e.target.value)} 
              className="period-select"
              aria-label="Selectionner la periode"
            >
              <option value="today">Aujourd'hui</option>
              <option value="week">7 derniers jours</option>
              <option value="month">30 derniers jours</option>
              <option value="quarter">3 derniers mois</option>
              <option value="year">Annee derniere</option>
            </select>
          </div>
          
          <button onClick={() => handleExport('sales')} className="export-button" aria-label="Exporter les donnees">
            <Download size={14} />
            <span>Exporter</span>
          </button>
        </div>
      </nav>

      {/* Key Metrics Grid - Fixed Layout */}
        <div className={`metrics-grid ${mounted ? 'mounted' : ''}`}>
          <MetricCard
            icon={<Coins />}
            title="Revenu net"
            value={`DT ${revenueNet.toFixed(2)}`}
            subtitle={`Brut : DT ${revenueGross.toFixed(2)}`}
          trend="+12.5%"
          color={COLORS.success}
          progress={85}
          mounted={mounted}
        />
        <MetricCard
          icon={<ShoppingCart />}
          title="Total commandes"
          value={ordersTotal}
            subtitle={`Valeur moyenne : DT ${revenueAvgOrderValue.toFixed(2)}`}
          trend="+8.3%"
          color={COLORS.primary}
          progress={72}
          mounted={mounted}
        />
        <MetricCard
          icon={<Package />}
          title="Produits"
          value={productsTotal}
          subtitle={`${productsOutOfStock} en rupture`}
          trend={productsLowStock > 0 ? `-${productsLowStock}` : '+2.1%'}
          color={COLORS.warning}
          progress={productsTotal > 0 ? ((productsTotal - productsOutOfStock) / productsTotal * 100) : 100}
          mounted={mounted}
        />
        <MetricCard
          icon={<Users />}
          title="Clients"
          value={customersTotal}
          subtitle={`${customersNew} nouveaux`}
          trend="+15.7%"
          color={COLORS.info}
          progress={68}
          mounted={mounted}
        />
        <MetricCard
          icon={<Star />}
          title="Note moyenne"
          value={reviewsAvgRating.toFixed(2)}
          subtitle={`${reviewsTotal} avis`}
          trend="+0.3"
          color={COLORS.purple}
          progress={reviewsAvgRating * 20}
          mounted={mounted}
        />
        <MetricCard
          icon={<MessageSquare />}
          title="Chats support"
          value={chatTotalConversations}
          subtitle={`${chatOpen} ouverts`}
          trend={chatOpen > 5 ? 'warning' : 'normal'}
          color={COLORS.pink}
          progress={chatOpen > 0 ? Math.max(0, 100 - (chatOpen * 10)) : 100}
          mounted={mounted}
        />
        <MetricCard
          icon={<Target />}
          title="Taux de conversion"
          value={`${conversionRate}%`}
          subtitle="Commandes livrees"
          trend="+2.1%"
          color={COLORS.teal}
          progress={parseFloat(conversionRate)}
          mounted={mounted}
        />
        <MetricCard
          icon={<Percent />}
          title="Impact des remises"
            value={`DT ${revenueDiscounts.toFixed(2)}`}
          subtitle={`${discountRate.toFixed(1)}% du brut`}
          trend="normal"
          color={COLORS.indigo}
          progress={100 - discountRate}
          mounted={mounted}
        />
      </div>

      {/* Tab Content - Fixed Structure */}
      <div className="tab-content">
        {activeTab === 'overview' && (
          <>
            <SectionHeader icon={Activity} title="Apercu des performances" mounted={mounted} />
            
            <div className="charts-row">
              <ChartCard
                title="Tendance revenu et commandes"
                subtitle={`Performance quotidienne - ${periodLabel}`}
                icon={TrendingUp}
                color={COLORS.success}
                large
                mounted={mounted}
                contentHeight={chartLargeHeight}
                isLoading={chartLoading}
              >
                {({ width, height }) => (
                  <ComposedChart width={width} height={height} data={trendData}>
                      <defs>
                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.3}/>
                          <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0.05}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke="rgba(237, 242, 247, 0.8)" vertical={false} />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fill: '#718096', fontSize: 11 }} 
                        axisLine={false} 
                        tickLine={false} 
                      />
                      <YAxis 
                        yAxisId="left"
                        tick={{ fill: '#718096', fontSize: 11 }} 
                        axisLine={false} 
                        tickLine={false} 
                      />
                      <YAxis 
                        yAxisId="right" 
                        orientation="right" 
                        tick={{ fill: '#718096', fontSize: 11 }} 
                        axisLine={false} 
                        tickLine={false} 
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend wrapperStyle={{ paddingTop: '12px', fontSize: '12px' }} />
                      <Area 
                        yAxisId="left"
                        type="monotone" 
                        dataKey="revenue" 
                        stroke={COLORS.primary} 
                        fill="url(#colorRevenue)" 
                        strokeWidth={2}
                        name="Revenu (DT)"
                        dot={{ r: 3, strokeWidth: 2, stroke: '#fff', fill: COLORS.primary }}
                        activeDot={{ r: 5, strokeWidth: 3, stroke: '#fff' }}
                        animationDuration={800}
                      />
                      <Bar 
                        yAxisId="right" 
                        dataKey="orders" 
                        fill={COLORS.info} 
                        radius={[4, 4, 0, 0]} 
                        name="Commandes"
                        animationDuration={600}
                      />
                    </ComposedChart>
                )}
              </ChartCard>

              <ChartCard
                title="Radar de performance"
                subtitle="Sante globale de l activite"
                icon={Zap}
                color={COLORS.warning}
                mounted={mounted}
                contentHeight={chartHeight}
                isLoading={chartLoading}
              >
                {({ width, height }) => (
                  <RadarChart width={width} height={height} data={performanceData}>
                      <PolarGrid stroke="rgba(226, 232, 240, 0.6)" />
                      <PolarAngleAxis dataKey="metric" tick={{ fill: '#4A5568', fontSize: 11 }} />
                      <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: '#A0AEC0', fontSize: 10 }} />
                      <Radar 
                        name="Performance" 
                        dataKey="value" 
                        stroke={COLORS.primary} 
                        fill={COLORS.primary} 
                        fillOpacity={0.25} 
                        strokeWidth={2}
                        animationDuration={800}
                      />
                      <Tooltip />
                    </RadarChart>
                )}
              </ChartCard>
            </div>

            <div className="charts-row">
              <ChartCard
                title="Repartition des statuts"
                subtitle="Commandes par statut"
                icon={ShoppingCart}
                color={COLORS.primary}
                mounted={mounted}
                contentHeight={chartHeight}
                isLoading={chartLoading}
              >
                {({ width, height }) => (
                  <PieChart width={width} height={height}>
                      <Pie
                        data={statusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey="value"
                        animationDuration={800}
                      >
                        {statusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomPieTooltip />} />
                    </PieChart>
                )}
              </ChartCard>

              <ChartCard
                title="Types de clients"
                subtitle="Comparaison invites vs inscrits"
                icon={Users}
                color={COLORS.info}
                mounted={mounted}
                contentHeight={chartHeight}
                isLoading={chartLoading}
              >
                {({ width, height }) => (
                  <BarChart width={width} height={height} data={userTypeData}>
                      <CartesianGrid stroke="rgba(237, 242, 247, 0.8)" vertical={false} />
                      <XAxis 
                        dataKey="name" 
                        tick={{ fill: '#718096', fontSize: 11 }} 
                        axisLine={false} 
                        tickLine={false} 
                      />
                      <YAxis 
                        tick={{ fill: '#718096', fontSize: 11 }} 
                        axisLine={false} 
                        tickLine={false} 
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar 
                        dataKey="orders" 
                        fill={COLORS.info} 
                        radius={[6, 6, 0, 0]} 
                        name="Commandes"
                        animationDuration={600}
                      />
                      <Bar 
                        dataKey="revenue" 
                        fill={COLORS.success} 
                        radius={[6, 6, 0, 0]} 
                        name="Revenu (DT)"
                        animationDuration={600}
                        animationBegin={100}
                      />
                    </BarChart>
                )}
              </ChartCard>
            </div>
          </>
        )}

        {activeTab === 'sales' && (
          <>
              <SectionHeader icon={Coins} title="Analyse des ventes" mounted={mounted} />
            
            <div className="stats-row">
                <StatCardLarge 
                  title="Revenu brut" 
                  value={`DT ${revenueGross.toFixed(2)}`} 
                  icon={<Coins />}
                  color={COLORS.success}
                  details={[
                    { label: 'Revenu net', value: `DT ${revenueNet.toFixed(2)}` },
                    { label: 'Remises', value: `-DT ${revenueDiscounts.toFixed(2)}` },
                    { label: 'Frais de livraison', value: `+DT ${revenueDeliveryFees.toFixed(2)}` }
                  ]}
                mounted={mounted}
              />
              <StatCardLarge 
                title="Repartition des commandes" 
                value={ordersTotal}
                icon={<ShoppingCart />}
                color={COLORS.primary}
                details={ordersByStatus.slice(0, 3).map(status => ({
                  label: getStatusLabel(status.status ?? status.order_status ?? status.state ?? status.name ?? 'unknown'),
                  value: asNumber(status.count ?? status.order_count ?? status.orders ?? status.value)
                }))}
                mounted={mounted}
              />
                <StatCardLarge 
                  title="Statistiques de livraison" 
                  value={`DT ${deliveryTotalRevenue.toFixed(2)}`}
                  icon={<Package />}
                  color={COLORS.warning}
                  details={[
                    { label: 'Livraisons payees', value: deliveryPaidDeliveries },
                    { label: 'Livraisons gratuites', value: deliveryFreeDeliveries },
                    { label: 'Frais moyen', value: `DT ${deliveryAvgFee.toFixed(2)}` }
                  ]}
                mounted={mounted}
              />
            </div>

            <div className="charts-row">
              <ChartCard
                title="Repartition des revenus"
                subtitle={`Composants du revenu - ${periodLabel}`}
                icon={BarChart3}
                color={COLORS.primary}
                large
                mounted={mounted}
                contentHeight={chartLargeHeight}
                isLoading={chartLoading}
              >
                {({ width, height }) => (
                  <AreaChart width={width} height={height} data={trendData}>
                      <defs>
                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.25}/>
                          <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0.05}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke="rgba(237, 242, 247, 0.8)" vertical={false} />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fill: '#718096', fontSize: 11 }} 
                        axisLine={false} 
                        tickLine={false} 
                      />
                      <YAxis 
                        tick={{ fill: '#718096', fontSize: 11 }} 
                        axisLine={false} 
                        tickLine={false} 
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend wrapperStyle={{ paddingTop: '12px', fontSize: '12px' }} />
                      <Area 
                        type="monotone" 
                        dataKey="revenue" 
                        stackId="1"
                        stroke={COLORS.primary} 
                        fill="url(#colorRevenue)" 
                        name="Revenu (DT)"
                        strokeWidth={2}
                        animationDuration={800}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="units" 
                        stackId="1"
                        stroke={COLORS.info} 
                        fill="rgba(49, 130, 206, 0.1)" 
                        name="Unites vendues"
                        strokeWidth={2}
                        animationDuration={800}
                        animationBegin={100}
                      />
                    </AreaChart>
                )}
              </ChartCard>
            </div>
          </>
        )}

        {activeTab === 'products' && (
          <>
            <SectionHeader icon={Package} title="Performance des produits" mounted={mounted} />
            
            <div className="charts-row">
              <ChartCard
                title="Top 8 produits"
                subtitle="Meilleures ventes par unites"
                icon={Package}
                color={COLORS.success}
                large
                mounted={mounted}
                contentHeight={chartLargeHeight}
                isLoading={chartLoading}
              >
                {({ width, height }) => (
                  <BarChart width={width} height={height} data={topProductsData} layout="vertical">
                      <CartesianGrid stroke="rgba(237, 242, 247, 0.8)" />
                      <XAxis 
                        type="number" 
                        tick={{ fill: '#718096', fontSize: 11 }} 
                        axisLine={false} 
                        tickLine={false} 
                      />
                      <YAxis 
                        dataKey="name" 
                        type="category" 
                        tick={{ fill: '#718096', fontSize: 11 }} 
                        width={130} 
                        axisLine={false} 
                        tickLine={false} 
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar 
                        dataKey="sales" 
                        fill={COLORS.success} 
                        radius={[0, 6, 6, 0]} 
                        name="Unites vendues"
                        animationDuration={700}
                      />
                      <Bar 
                        dataKey="orders" 
                        fill={COLORS.info} 
                        radius={[0, 6, 6, 0]} 
                        name="Commandes"
                        animationDuration={700}
                        animationBegin={100}
                      />
                    </BarChart>
                )}
              </ChartCard>

              <ChartCard
                title="Revenu par categorie"
                subtitle="Performance des ventes par categorie"
                icon={Tag}
                color={COLORS.warning}
                mounted={mounted}
                contentHeight={chartHeight}
                isLoading={chartLoading}
              >
                {({ width, height }) => (
                  <BarChart width={width} height={height} data={categoryData}>
                      <CartesianGrid stroke="rgba(237, 242, 247, 0.8)" vertical={false} />
                      <XAxis 
                        dataKey="name" 
                        tick={{ fill: '#718096', fontSize: 11 }} 
                        angle={-45} 
                        textAnchor="end" 
                        height={70} 
                        axisLine={false} 
                        tickLine={false} 
                      />
                      <YAxis 
                        tick={{ fill: '#718096', fontSize: 11 }} 
                        axisLine={false} 
                        tickLine={false} 
                      />
                      <Tooltip 
                        content={<CustomTooltip />} 
                        formatter={(value) => `DT ${value.toFixed(2)}`} 
                      />
                      <Bar 
                        dataKey="value" 
                        fill={COLORS.warning} 
                        radius={[4, 4, 0, 0]}
                        animationDuration={600}
                      >
                        {categoryData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                )}
              </ChartCard>
            </div>

            <div className="inventory-alerts">
              <AlertCard 
                type="critical"
                title="Rupture de stock"
                value={`${productsOutOfStock} produits`}
                icon={<AlertCircle />}
                mounted={mounted}
              />
              <AlertCard 
                type="warning"
                title="Stock faible"
                value={`${productsLowStock} produits`}
                icon={<Clock />}
                mounted={mounted}
              />
              <AlertCard 
                type="success"
                title="En stock"
                value={`${productsTotal - productsOutOfStock - productsLowStock} produits`}
                icon={<CheckCircle />}
                mounted={mounted}
              />
            </div>
          </>
        )}

        {activeTab === 'customers' && (
          <>
            <SectionHeader icon={Users} title="Apercu clients" mounted={mounted} />
            
            <div className="stats-grid-detailed">
              <StatCard title="Total clients" value={customersTotal} icon={<Users />} color={COLORS.info} mounted={mounted} />
              <StatCard title="Nouveaux clients" value={customersNew} icon={<TrendingUp />} color={COLORS.success} mounted={mounted} />
              <StatCard 
                title="Commandes invites" 
                value={`${guestVsRegistered.find(g => g.type === 'Guest')?.percentage || 0}%`} 
                icon={<ShoppingCart />} 
                color={COLORS.warning} 
                mounted={mounted}
              />
              <StatCard 
                title="Commandes inscrits" 
                value={`${guestVsRegistered.find(g => g.type === 'Registered')?.percentage || 0}%`} 
                icon={<CheckCircle />} 
                color={COLORS.primary} 
                mounted={mounted}
              />
              <StatCard title="Valeur moyenne commande" value={`DT ${revenueAvgOrderValue.toFixed(2)}`} icon={<Coins />} color={COLORS.success} mounted={mounted} />
              <StatCard title="Total avis" value={reviewsTotal} icon={<Star />} color={COLORS.purple} mounted={mounted} />
            </div>
          </>
        )}
      </div>

      {/* Additional Metrics - Fixed Section */}
      <div className={`section-header ${mounted ? 'mounted' : ''}`}>
        <h2 className="section-title">
          <TrendingUpIcon size={20} />
          <span>Indicateurs supplementaires</span>
          <div className="section-divider" />
        </h2>
      </div>
      
      <div className={`stats-grid ${mounted ? 'mounted' : ''}`}>
        <StatCard title="Promos actives" value={promosActive} icon={<Tag />} color={COLORS.warning} mounted={mounted} />
        <StatCard title="Utilisations promo" value={promosTotalUses} icon={<CheckCircle />} color={COLORS.success} mounted={mounted} />
        <StatCard title="Messages chat" value={chatTotalMessages} icon={<MessageSquare />} color={COLORS.pink} mounted={mounted} />
        <StatCard 
          title="Chats ouverts" 
          value={chatOpen} 
          icon={<Clock />} 
          color={chatOpen > 5 ? COLORS.warning : COLORS.success} 
          mounted={mounted}
        />
        <StatCard title="Chats fermes" value={chatClosed} icon={<CheckCircle />} color={COLORS.info} mounted={mounted} />
        <StatCard title="Produits evalues" value={reviewsProductsWithReviews} icon={<Star />} color={COLORS.purple} mounted={mounted} />
        <StatCard title="Categories" value={categoriesList.length} icon={<Tag />} color={COLORS.indigo} mounted={mounted} />
        <StatCard title="Note moyenne" value={reviewsAvgRating.toFixed(2)} icon={<Star />} color={COLORS.amber} mounted={mounted} />
      </div>
    </div>
  );
};

// Reusable Components - Enhanced with Mount Animation
const SectionHeader = ({ icon: Icon, title, mounted }) => (
  <div className={`section-header ${mounted ? 'mounted' : ''}`}>
    <h2 className="section-title">
      <Icon size={20} />
      <span>{title}</span>
      <div className="section-divider" />
    </h2>
  </div>
);

const ChartCard = ({ title, subtitle, icon: Icon, color, children, large, mounted, contentHeight, isLoading }) => {
  const contentRef = useRef(null);
  const { width, height } = useElementSize(contentRef);
  const resolvedHeight = contentHeight ?? height;
  const canRenderChart = width > 0 && resolvedHeight > 0;
  const showSkeleton = isLoading || !canRenderChart;

  return (
    <div className={`chart-card ${large ? 'chart-large' : ''} ${mounted ? 'mounted' : ''}`}>
      <div className="chart-header">
        <div>
          <h3 className="chart-title">{title}</h3>
          <p className="chart-subtitle">{subtitle}</p>
        </div>
        <div className="chart-icon" style={{ color }}>
          <Icon size={18} />
        </div>
      </div>
      <div
        className="chart-content"
        ref={contentRef}
        style={contentHeight ? { height: contentHeight } : undefined}
      >
        {typeof children === 'function'
          ? showSkeleton
            ? <ChartSkeleton />
            : children({ width, height: resolvedHeight })
          : children}
      </div>
    </div>
  );
};

const MetricCard = ({ icon, title, value, subtitle, trend, color, progress, mounted }) => (
  <div className={`metric-card ${mounted ? 'mounted' : ''}`}>
    <div className="metric-icon-wrapper" style={{ backgroundColor: `${color}15`, color }}>
      {React.cloneElement(icon, { size: 20 })}
    </div>
    <div className="metric-content">
      <p className="metric-title">{title}</p>
      <h3 className="metric-value">{value}</h3>
      <p className="metric-subtitle">{subtitle}</p>
      {progress !== undefined && (
        <div className="metric-progress">
          <div 
            className="progress-bar" 
            style={{ 
              width: `${Math.min(progress, 100)}%`, 
              background: `linear-gradient(90deg, ${color}, ${color}AA)` 
            }} 
          />
        </div>
      )}
    </div>
    {trend !== 'warning' && trend !== 'normal' && (
      <div className={`trend-badge ${trend.startsWith('+') ? 'positive' : 'negative'}`}>
        {trend.startsWith('+') ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
        <span>{trend}</span>
      </div>
    )}
  </div>
);

const StatCard = ({ title, value, icon, color = '#E53E3E', mounted }) => (
  <div className={`stat-card ${mounted ? 'mounted' : ''}`}>
    <div className="stat-icon" style={{ backgroundColor: `${color}10`, color }}>
      {React.cloneElement(icon, { size: 18 })}
    </div>
    <div>
      <p className="stat-title">{title}</p>
      <p className="stat-value">{value}</p>
    </div>
  </div>
);

const StatCardLarge = ({ title, value, icon, color, details, mounted }) => (
  <div className={`stat-card-large ${mounted ? 'mounted' : ''}`} style={{ borderLeftColor: color }}>
    <div className="stat-large-header">
      <div className="stat-large-icon" style={{ backgroundColor: `${color}10`, color }}>
        {React.cloneElement(icon, { size: 22 })}
      </div>
      <div>
        <p className="stat-large-title">{title}</p>
        <h3 className="stat-large-value">{value}</h3>
      </div>
    </div>
    <div className="stat-large-details">
      {details.map((detail, index) => (
        <div key={index} className="stat-detail-row">
          <span className="stat-detail-label">{detail.label}</span>
          <span className="stat-detail-value">{detail.value}</span>
        </div>
      ))}
    </div>
  </div>
);

const AlertCard = ({ type, title, value, icon, mounted }) => {
  const colors = {
    critical: '#E53E3E',
    warning: '#D69E2E',
    success: '#38A169'
  };
  
  return (
    <div className={`alert-card alert-${type} ${mounted ? 'mounted' : ''}`}>
      <div className="alert-icon" style={{ color: colors[type] }}>
        {React.cloneElement(icon, { size: 20 })}
      </div>
      <div>
        <h4>{title}</h4>
        <p>{value}</p>
      </div>
    </div>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <div className="custom-tooltip">
      <p className="tooltip-label">{label}</p>
      {payload.map((entry, index) => {
        const isCurrency = entry.name?.includes('DT') || entry.dataKey === 'revenue';
        const isPercent = entry.name?.includes('%');
        return (
          <div key={index} className="tooltip-item">
            <span 
              className="tooltip-color" 
              style={{ 
                backgroundColor: entry.color,
                borderRadius: isCurrency ? '50%' : '2px'
              }}
            ></span>
            <span className="tooltip-name">{entry.name}:</span>
            <span className="tooltip-value">
              {typeof entry.value === 'number' 
                ? isCurrency
                  ? `DT ${Number(entry.value).toFixed(2)}`
                  : isPercent
                  ? `${Number(entry.value).toFixed(1)}%`
                  : entry.value.toLocaleString()
                : entry.value}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const CustomPieTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const data = payload[0];
  return (
    <div className="custom-tooltip">
      <p className="tooltip-label">{data.name}</p>
      <div className="tooltip-item">
        <span 
          className="tooltip-color" 
          style={{ 
            backgroundColor: data.color,
            borderRadius: '50%'
          }}
        ></span>
        <span className="tooltip-name">Commandes :</span>
        <span className="tooltip-value">{data.value.toLocaleString()}</span>
      </div>
      {data.payload?.revenue && (
        <div className="tooltip-item">
          <span className="tooltip-color"></span>
          <span className="tooltip-name">Revenu (DT) :</span>
          <span className="tooltip-value">DT {Number(data.payload.revenue).toFixed(2)}</span>
        </div>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
